import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { api, type CsGroup } from "@/lib/api";
import { useCan } from "@/lib/shell-context";
import { Button, Empty, ErrorNote, Loading, Mono, Panel, Status } from "@/components/primitives";

/**
 * CS group (spec §9, decision 23).
 *
 * Groups appear on their own; activating one is a deliberate act. That gap is
 * the safety property — until someone activates a group the bot ignores it
 * completely, so adding the bot to a group can never start leaking player
 * data into it.
 */

const CHECKS: { key: string; label: string; why: string }[] = [
  { key: "is_admin", label: "Bot is an administrator", why: "Needed to create Topics at all." },
  {
    key: "can_manage_topics",
    label: "Can manage topics",
    why: "Each player needs their own Topic.",
  },
  {
    key: "can_delete_messages",
    label: "Can delete messages",
    why: "Needed for /revoke to recall a message.",
  },
];

function Checks({ group }: { group: CsGroup }) {
  const perms = group.perms ?? {};
  const rows = [
    {
      key: "is_forum",
      label: "Topics are enabled",
      why: "The group must be a forum.",
      ok: group.isForum,
    },
    ...CHECKS.map((check) => ({ ...check, ok: perms[check.key] === true })),
  ];

  return (
    <ul className="grid gap-1.5">
      {rows.map((row) => (
        <li key={row.key} className="flex items-start gap-2">
          <span className={row.ok ? "text-ok" : "text-destructive"} aria-hidden>
            {row.ok ? "✓" : "✕"}
          </span>
          <span>
            {row.label}
            {!row.ok && <span className="ml-2 text-muted-foreground">{row.why}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function GroupsPage() {
  const { botId } = useParams();
  const canManage = useCan("groups", "manage");
  const queryClient = useQueryClient();

  const groups = useQuery({
    queryKey: ["groups", botId],
    queryFn: () => api.get<CsGroup[]>(`/bots/${botId}/groups`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["groups", botId] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard", botId] });
  };

  const activate = useMutation({
    mutationFn: (id: number) => api.post(`/groups/${id}/activate`),
    onSuccess: invalidate,
  });

  const leave = useMutation({
    mutationFn: (id: number) => api.post(`/groups/${id}/leave`),
    onSuccess: invalidate,
  });

  if (groups.isLoading) return <Loading />;
  if (groups.error) return <ErrorNote error={groups.error} />;

  const all = groups.data ?? [];
  const active = all.find((g) => g.status === "active");
  const discovered = all.filter((g) => g.status === "discovered");
  const left = all.filter((g) => g.status === "left");

  return (
    <div className="grid grid-cols-1 gap-4">
      {activate.error && <ErrorNote error={activate.error} />}

      <Panel title="Active CS group">
        {active ? (
          <div className="grid gap-3 px-5 py-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="min-w-0 font-medium break-words">
                {active.title ?? "Untitled group"}
              </span>
              <Mono className="text-muted-foreground">{active.telegramChatId}</Mono>
              <Status value="active" />
              {active.lastCheckedAt && (
                <span className="w-full text-muted-foreground sm:ml-auto sm:w-auto">
                  Checked {new Date(active.lastCheckedAt).toLocaleString()}
                </span>
              )}
            </div>
            <Checks group={active} />
          </div>
        ) : (
          <Empty
            title="No group is activated"
            hint="Add the bot to a Topics-enabled supergroup and promote it to administrator. It appears below, and nothing is delivered until you choose it here."
          />
        )}
      </Panel>

      <Panel title="Groups the bot has been added to">
        {discovered.length === 0 ? (
          <Empty
            title="Nothing waiting"
            hint="A group shows up here the moment the bot is added to it."
          />
        ) : (
          <ul className="divide-y divide-border/60">
            {discovered.map((group) => (
              <li key={group.id} className="grid gap-2.5 px-5 py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="min-w-0 font-medium break-words">
                    {group.title ?? "Untitled group"}
                  </span>
                  <Mono className="text-muted-foreground">{group.telegramChatId}</Mono>
                  {canManage && (
                    <div className="flex w-full gap-2 sm:ml-auto sm:w-auto">
                      <Button
                        className="flex-1 sm:flex-none"
                        onClick={() => activate.mutate(group.id)}
                        disabled={activate.isPending}
                      >
                        Use as CS group
                      </Button>
                      <Button
                        className="flex-1 sm:flex-none"
                        variant="quiet"
                        onClick={() => leave.mutate(group.id)}
                        disabled={leave.isPending}
                      >
                        Leave group
                      </Button>
                    </div>
                  )}
                </div>
                <Checks group={group} />
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {active && <RefreshNames botId={botId} />}

      {left.length > 0 && (
        <Panel title="Left">
          <ul className="divide-y divide-border/60">
            {left.map((group) => (
              <li
                key={group.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-5 py-2 opacity-70"
              >
                <span>{group.title ?? "Untitled group"}</span>
                <Mono className="text-muted-foreground">{group.telegramChatId}</Mono>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

interface RefreshResult {
  total: number;
  checked: number;
  renamed: number;
  failed: number;
  nextOffset: number | null;
  retryAfter: number | null;
}

/**
 * Renames every Topic to the current naming rule (decision 37), batch by
 * batch, waiting whenever Telegram asks. Topics also correct themselves the
 * next time their player writes; this is for doing them all now.
 */
function RefreshNames({ botId }: { botId: string | undefined }) {
  const canEdit = useCan("groups", "manage");
  const [progress, setProgress] = useState<{ done: number; total: number; renamed: number } | null>(
    null,
  );
  const [waiting, setWaiting] = useState<number | null>(null);

  const run = useMutation({
    mutationFn: async () => {
      let offset: number | null = 0;
      let renamed = 0;
      while (offset !== null) {
        const result: RefreshResult = await api.post(`/bots/${botId}/topics/refresh-names`, {
          offset,
        });
        renamed += result.renamed;
        setProgress({ done: result.nextOffset ?? result.total, total: result.total, renamed });
        if (result.retryAfter !== null) {
          setWaiting(result.retryAfter);
          await new Promise((r) => setTimeout(r, (result.retryAfter! + 1) * 1000));
          setWaiting(null);
        }
        offset = result.nextOffset;
      }
    },
  });

  if (!canEdit) return null;
  const pct =
    progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <Panel title="Topic names">
      <div className="grid gap-3 px-5 py-3">
        <p className="text-muted-foreground">
          Names read <span className="mono text-foreground">Source · #01 · Name</span>. Topics
          update on their own when the player next writes; this updates them all now.
        </p>
        {progress && (
          <div className="grid gap-1.5">
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-muted-foreground">
              {progress.done} of {progress.total} checked · {progress.renamed} renamed
              {waiting !== null && ` · Telegram asked us to wait ${waiting}s`}
              {!run.isPending && run.isSuccess && " · done"}
            </span>
          </div>
        )}
        {run.error && <ErrorNote error={run.error} />}
        <Button
          className="justify-self-start"
          variant="quiet"
          disabled={run.isPending}
          onClick={() => run.mutate()}
        >
          {run.isPending ? "Updating…" : "Update all Topic names"}
        </Button>
      </div>
    </Panel>
  );
}
